<script lang="ts">
  import type { Snippet } from "svelte";
  import type { ClassValue } from "svelte/elements";
  import { fade } from "svelte/transition";

  interface Props {
    children?: Snippet;
    class?: ClassValue;
    colspan?: number;
    fixedHeight?: boolean;
  }

  let { children, class: className, colspan, fixedHeight = true }: Props = $props();

  let scrollContainer: HTMLDivElement;
  let canScrollLeft = $state(false);
  let canScrollRight = $state(false);

  function updateScrollState(element: HTMLDivElement) {
    const { scrollLeft, scrollWidth, clientWidth } = element;
    canScrollLeft = scrollLeft > 0;
    canScrollRight = scrollLeft < scrollWidth - clientWidth - 1;
  }

  function handleScroll(event: Event) {
    const target = event.target as HTMLDivElement;
    updateScrollState(target);
  }

  $effect(() => {
    if (scrollContainer) {
      updateScrollState(scrollContainer);

      if (typeof ResizeObserver !== "undefined") {
        const resizeObserver = new ResizeObserver(() => {
          updateScrollState(scrollContainer);
        });
        resizeObserver.observe(scrollContainer);

        return () => {
          resizeObserver.disconnect();
        };
      }
    }
  });
</script>

<td
  {colspan}
  class={["max-w-[300px] truncate border-t border-alpha-050 pr-8", className]}
  style={fixedHeight ? "height: 40px;" : ""}
>
  <div class="cell-container relative flex h-full items-center">
    <div
      bind:this={scrollContainer}
      class="scrollbar-hide flex min-h-0 flex-1 gap-2 overflow-x-auto px-4 whitespace-nowrap"
      onscroll={handleScroll}
    >
      {@render children?.()}
    </div>

    <!-- left gradient overlay -->
    {#if canScrollLeft}
      <div
        class="gradient-left pointer-events-none absolute top-0 left-0 h-full w-8 bg-gradient-to-r from-mono-000 to-transparent"
        transition:fade={{ duration: 150 }}
      ></div>
    {/if}

    <!-- right gradient overlay -->
    {#if canScrollRight}
      <div
        class="gradient-right pointer-events-none absolute top-0 right-0 h-full w-8 bg-gradient-to-l from-mono-000 to-transparent"
        transition:fade={{ duration: 150 }}
      ></div>
    {/if}
  </div>
</td>

<style>
  @reference "#tailwind.css";
  .cell-container {
    @apply w-full;
  }

  .scrollbar-hide {
    -ms-overflow-style: none;
    scrollbar-width: none;
  }
  .scrollbar-hide::-webkit-scrollbar {
    display: none;
  }

  .gradient-left,
  .gradient-right {
    z-index: 10;
    transition: opacity 0.2s ease-in-out;
  }
</style>
